"use client"

import {
    createSupplier as apiCreateSupplier,
    updateSupplierById as apiUpdateSupplier,
    deleteSupplierById,
    fetchSupplierById,
    fetchSuppliers,
} from "@/(admin)/modules/suppliers/services/suppliers"
import { SUPPLIERS } from "@/constants/admin-paths"
import { useCheckPermission } from "@/hook/use-check-permission"
import { t } from "@/languages/i18n"
import { normalizeText } from "@/lib/external-components"
import { SupllierListResponseDataItem, SupllierRequest, SupllierResponseDataObject } from "@/types/codegen/data-contracts"
import { App } from "antd"
import { useRouter, useSearchParams } from "next/navigation"
import queryString from "query-string"
import { useCallback, useState } from "react"

interface PaginationMeta {
    pagination?: {
        page?: number
        pageSize?: number
        pageCount?: number
        total?: number
    }
}

export const useSuppliers = () => {
    const router = useRouter()
    const searchParams = useSearchParams()
    const { message, modal } = App.useApp()

    const [dataItem, setDataItem] = useState<SupllierListResponseDataItem[]>([])
    const [loading, setLoading] = useState(false)
    const [metaData, setMetaData] = useState<PaginationMeta>()
    const [totalItem, setTotalItem] = useState(0)
    const [suppliersOptions, setSuppliersOptions] = useState<{ value: string | number; label: string }[]>([])
    const [filterTabStore, setFilterTabStore] = useState("all")
    const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([])
    const [bulkAction, setBulkAction] = useState<string>("")
    const [needRefresh, setNeedRefresh] = useState<boolean>(false)
    const [supplierDetail, setSupplierDetail] = useState<SupllierResponseDataObject | null>(null);
    const [pagination, setPagination] = useState<{
        page: number
        pageSize: number
        total: number
        pageCount: number
    }>({
        page: 1,
        pageSize: 20,
        total: 0,
        pageCount: 0,
    })

    const { hasPermission: checkPermission } = useCheckPermission()
    const hasPermission = useCallback((action: string) => {
        return checkPermission("supllier", action)
    }, [checkPermission]);

    const fetchSuppliersData = useCallback(
        async (searchTerms?: { [key: string]: string | number | object }) => {
            setLoading(true)
            setDataItem([])
            try {
                const params = {
                    page: String(pagination.page),
                    pageSize: String(pagination.pageSize),
                    ...searchTerms,
                }
                const allResponse = await fetchSuppliers(params)
                const response = allResponse && allResponse.data
                const meta = allResponse && allResponse.meta
                setMetaData(meta)

                if (response && response.length > 0) {
                    setDataItem(response)

                    setSuppliersOptions(
                        response.map((suppliers: SupllierListResponseDataItem) => ({
                            value: suppliers.id || 0,
                            label: suppliers.attributes?.fullname || "",
                        })),
                    )
                    setLoading(false)
                } else {
                    setLoading(false)
                }

                if (meta?.pagination) {
                    setPagination({
                        page: meta.pagination.page || 1,
                        pageSize: meta.pagination.pageSize || 20,
                        total: meta.pagination.total || 0,
                        pageCount: meta.pagination.pageCount || 0,
                    })
                }

                return response
            } catch (error) {
                message.error(t("Không thể lấy danh sách nhà cung cấp. Vui lòng thử lại."))
            }
        },
        [message, pagination.page, pagination.pageSize],
    )

    const getSupplierInformation = useCallback(async (id: number) => {
        setLoading(true);
        try {
            const res = await fetchSupplierById(id);
            setSupplierDetail(res);
            return res;
        } catch (error) {
            message.error(t("Lỗi lấy thông tin nhà cung cấp"));
        } finally {
            setLoading(false);
        }
    }, []);

    const createSupplier = useCallback(async (data: SupllierRequest) => {
        setLoading(true);
        try {
            const res = await apiCreateSupplier(data);
            message.success(t("Thêm nhà cung cấp thành công"));
            return res;
        } catch (error) {
            message.error(t("Thêm nhà cung cấp thất bại"));
            return null;
        } finally {
            setLoading(false);
        }
    }, [message]);

    const updateSupplier = useCallback(async (id: number, data: SupllierRequest) => {
        setLoading(true);
        try {
            const res = await apiUpdateSupplier(id, data);
            message.success(t("Cập nhật nhà cung cấp thành công"));
            return res;
        } catch (error) {
            message.error(t("Cập nhật nhà cung cấp thất bại"));
            return null;
        } finally {
            setLoading(false);
        }
    }, [message]);

    const handleDuplicate = useCallback(async (record: SupllierListResponseDataItem) => {
        if (record) {
            setLoading(true);
            const attr = record.attributes;
            const baseName = attr?.fullname || "";
            const copyName = `${baseName} (Sao chép)`;

            const dataPayload = {
                data: {
                    ...attr,
                    fullname: copyName,
                },
            } as SupllierRequest;

            try {
                await apiCreateSupplier(dataPayload);
                message.success(t("Nhân bản nhà cung cấp thành công!"));
                const currentParams = Object.fromEntries(searchParams.entries());

                fetchSuppliersData(currentParams);
            } catch (error) {
                message.error(t("Không thể nhân bản nhà cung cấp. Vui lòng thử lại."));
            } finally {
                setLoading(false);
            }
        }
    }, [apiCreateSupplier, message, fetchSuppliersData, filterTabStore, searchParams]);

    const handleDelete = useCallback(
        async (id: number, filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
            modal.confirm({
                title: t("Xác nhận xóa"),
                content: t("Bạn có chắc chắn muốn xóa nhà cung cấp này?"),
                okText: t("Xóa"),
                cancelText: t("Hủy"),
                onOk: async () => {
                    try {
                        setLoading(true)
                        await deleteSupplierById(id)
                        const currentParams = Object.fromEntries(searchParams.entries());
                        fetchSuppliersData(currentParams)
                        message.success(t("Xóa nhà cung cấp thành công!"))
                    } catch {
                        message.error(t("Lỗi khi xóa nhà cung cấp"))
                    }
                },
            })
        },
        [message, fetchSuppliersData, filterTabStore, searchParams],
    )

    const handleBulkDelete = useCallback(
        async (filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
            try {
                setLoading(true)
                const ids = selectedRowKeys.map(id => Number(id))
                await Promise.all(ids.map(id => deleteSupplierById(id)))
                setSelectedRowKeys([])
                const currentParams = Object.fromEntries(searchParams.entries());
                fetchSuppliersData(currentParams)
                setLoading(false)
                message.success(t("Xóa nhà cung cấp thành công!"))
            } catch {
                message.error(t("Lỗi khi xóa nhà cung cấp"))
            }
        },
        [selectedRowKeys, message, fetchSuppliersData, filterTabStore, searchParams],
    )

    const handleApplyBulkAction = useCallback(
        async (filterValues?: Record<string, string>, sortPaginationValues?: Record<string, string>) => {
            if (selectedRowKeys.length === 0) {
                message.warning(t("Vui lòng chọn nhà cung cấp để thực hiện thao tác."));
                return;
            }

            if (bulkAction === "delete") {
                modal.confirm({
                    title: t("Xác nhận xóa"),
                    content: `${t("Bạn có chắc chắn muốn xóa")} ${selectedRowKeys.length} ${t("nhà cung cấp đã chọn?")}`,
                    okText: t("Xóa"),
                    cancelText: t("Hủy"),
                    onOk: () => handleBulkDelete(filterValues, sortPaginationValues),
                });
                return;
            }

            message.warning(t("Vui lòng chọn một thao tác hàng loạt hợp lệ."));
        },
        [bulkAction, selectedRowKeys, message, handleBulkDelete]
    );

    const handleEdit = useCallback(
        (id: number) => {
            router.push(`/admin/${SUPPLIERS.EDIT.PATH}?id=${id}`)
        },
        [router],
    )

    const handleRefresh = useCallback(
        (filterRef?: { current: { filterFunction: () => void } | null }) => {
            if (filterRef?.current) {
                filterRef.current.filterFunction()
            }
            setNeedRefresh(!needRefresh)
        },
        [needRefresh],
    )

    const handleSearch = useCallback(
        (filters: object, pathname: string) => {
            const values: { [key: string]: string } = {}

            Object.keys(filters).forEach(key => {
                const value = filters[key as keyof typeof filters]
                if (typeof value === "string") {
                    values[key] = value
                }
            })

            if (!values) {
                message.error("Giá trị không hợp lệ")
                return
            }

            let newValue = { ...values }

            if (values.search) {
                newValue = { ...newValue, search: normalizeText(values.search) }
            }

            const searchParams = queryString.stringify(newValue, { encode: false })
            const newUrl = searchParams.length > 0 ? `${pathname}?${searchParams}` : pathname
            router.push(`${newUrl}`)
        },
        [router, message],
    )

    return {
        dataItem,
        loading,
        metaData,
        totalItem,
        suppliersOptions,
        filterTabStore,
        selectedRowKeys,
        bulkAction,
        needRefresh,
        supplierDetail,
        pagination,

        actions: {
            fetchSuppliersData,
            getSupplierInformation,
            handleDelete,
            handleEdit,
            handleRefresh,
            handleSearch,
            setSelectedRowKeys,
            setBulkAction,
            setFilterTabStore,
            handleDuplicate,
            handleApplyBulkAction,
            handleBulkDelete,
            hasPermission,
            updateSupplier,
            createSupplier,
            setPagination,
        },

        setters: {
            setDataItem,
            setLoading,
            setMetaData,
            setTotalItem,
            setSuppliersOptions,
            setSelectedRowKeys,
            setBulkAction,
            setNeedRefresh,
        },
    }
}